import { ButtonType } from "@components/atoms/Button/types";
import React, { useState } from "react";
import { Button, Spacer, VDBText } from "@components/atoms";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import { StockItemRequestClass } from "@src/types/HistoryRequest";
import ApproveOrderPopup from "./ApproveOrderPopup";
import RequestDiscountModal from "./RequestDiscountModal";
import DeclineRequestModal from "./DeclineRequestModal";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { View } from "react-native";

type ButtonsContainerProps = {
  stockItemRequest: StockItemRequestClass;
  productType: any;
  modifyRequest: any;
};

const SalesPersonButtonsContainer = ({
  stockItemRequest,
  productType,
}: ButtonsContainerProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const [isDeclineDisabled, setDeclineDisable] = useState(
    stockItemRequest?.available_actions?.request_decline === false ||
      stockItemRequest?.track_status_key === "decline_requested" ||
      stockItemRequest?.track_status_key === "price_change_requested"
  );
  const [isApproveOrderDisabled, setApproveOrderDisable] = useState(
    stockItemRequest?.available_actions?.approve === false ||
      stockItemRequest?.track_status_key === "decline_requested" ||
      stockItemRequest?.track_status_key === "price_change_requested"
  );
  const [isRequestDiscountDisabled, setRequestDiscountDisable] = useState(
    stockItemRequest?.available_actions?.request_price_change === false ||
      stockItemRequest?.track_status_key === "decline_requested"
  );

  const [approveCodeShowPopup, setApproveCodeShowPopup] = useState(false);
  const [declineRequestShowPopup, setDeclineRequestShowPopup] = useState(false);
  const [requestDiscountShowPopup, setRequestDiscountShowPopup] =
    useState(false);
  const [showPriceDisclaimer, setShowPriceDisclaimer] = useState(
    stockItemRequest?.track_status_key === "decline_requested" ||
      stockItemRequest?.track_status_key === "price_change_requested"
  );
  const [priceDisclaimerText, setPriceDisclaimerText] = useState(
    stockItemRequest?.track_status_key === "decline_requested"
      ? t(
          "requestHistoryDetail.declineRequest.title.text",
          "Request to decline the order has been sent"
        )
      : t(
          "requestHistoryDetail.priceRequestChange.title.text",
          "*Price change request submitted and pending."
        )
  );

  const buttonWidth = theme.isMobile ? "auto" : theme.isLargeScreen ? 224 : 180;

  return (
    <>
      {theme.isMobile ? (
        <MobileContainerParent>
          <MobileContainer>
            <FullWidthRow>
              <Button
                type={ButtonType.primary}
                width={buttonWidth}
                category={productType.theme}
                title={t(
                  "requestHistoryDetail.buttons.approveOrder.text",
                  "Approve Order"
                )}
                disabled={isApproveOrderDisabled}
                onPress={() => setApproveCodeShowPopup(true)}
              />
            </FullWidthRow>
            <Spacer size={SpaceType.Double} />
            <HalfRow>
              <HalfButtonContainer paddingRight={6}>
                <Button
                  type={ButtonType.secondary}
                  width={buttonWidth}
                  category={productType.theme}
                  title={t(
                    "requestHistoryDetail.buttons.requestDiscount.text",
                    "Request Discount"
                  )}
                  disabled={isRequestDiscountDisabled}
                  onPress={() => setRequestDiscountShowPopup(true)}
                />
              </HalfButtonContainer>
              <HalfButtonContainer paddingLeft={6}>
                <Button
                  type={ButtonType.tertiary}
                  width={buttonWidth}
                  category={productType.theme}
                  title={t(
                    "requestHistoryDetail.buttons.decline.text",
                    "Decline"
                  )}
                  disabled={isDeclineDisabled}
                  onPress={() => setDeclineRequestShowPopup(true)}
                />
              </HalfButtonContainer>
            </HalfRow>
          </MobileContainer>
          {showPriceDisclaimer && (
            <View style={{ marginTop: 10 }}>
              <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
                {priceDisclaimerText}
              </VDBText>
            </View>
          )}
        </MobileContainerParent>
      ) : (
        <WebContainerParent>
          <WebContainer>
            <ButtonFlexContainer>
              <Button
                type={ButtonType.tertiary}
                width={buttonWidth}
                category={productType.theme}
                title={t(
                  "requestHistoryDetail.buttons.decline.text",
                  "Decline"
                )}
                disabled={isDeclineDisabled}
                onPress={() => setDeclineRequestShowPopup(true)}
              />
            </ButtonFlexContainer>
            <RightButtonsContainer>
              <ButtonFlexContainer>
                <Button
                  type={ButtonType.secondary}
                  width={buttonWidth}
                  category={productType.theme}
                  title={t(
                    "requestHistoryDetail.buttons.requestDiscount.text",
                    "Request Discount"
                  )}
                  disabled={isRequestDiscountDisabled}
                  onPress={() => setRequestDiscountShowPopup(true)}
                />
              </ButtonFlexContainer>
              <ButtonFlexContainer>
                <Button
                  type={ButtonType.primary}
                  width={buttonWidth}
                  category={productType.theme}
                  title={t(
                    "requestHistoryDetail.buttons.approveOrder.text",
                    "Approve Order"
                  )}
                  disabled={isApproveOrderDisabled}
                  onPress={() => setApproveCodeShowPopup(true)}
                />
              </ButtonFlexContainer>
            </RightButtonsContainer>
          </WebContainer>
          {showPriceDisclaimer && (
            <View style={{ alignSelf: "flex-end", marginTop: 10 }}>
              <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
                {priceDisclaimerText}
              </VDBText>
            </View>
          )}
        </WebContainerParent>
      )}
      {approveCodeShowPopup && (
        <ApproveOrderPopup
          stockItemRequest={stockItemRequest}
          productType={productType}
          setApproveCodeShowPopup={setApproveCodeShowPopup}
          onSuccess={() => {
            setApproveOrderDisable(true);
            setDeclineDisable(true);
            setRequestDiscountDisable(true);
          }}
        />
      )}
      {requestDiscountShowPopup && (
        <RequestDiscountModal
          stockItemRequest={stockItemRequest}
          isOpen={requestDiscountShowPopup}
          onClose={() => {
            setRequestDiscountShowPopup(false);
          }}
          onSuccess={() => {
            setRequestDiscountShowPopup(false);
            setApproveOrderDisable(true);
            setDeclineDisable(true);
            setPriceDisclaimerText(
              t(
                "requestHistoryDetail.priceRequestChange.title.text",
                "*Price change request submitted and pending."
              )
            );
            setShowPriceDisclaimer(true);
          }}
        />
      )}
      {declineRequestShowPopup && (
        <DeclineRequestModal
          stockItemRequest={stockItemRequest}
          isOpen={declineRequestShowPopup}
          onClose={() => {
            setDeclineRequestShowPopup(false);
          }}
          onSuccess={() => {
            setDeclineRequestShowPopup(false);
            setApproveOrderDisable(true);
            setRequestDiscountDisable(true);
            setDeclineDisable(true);
            setPriceDisclaimerText(
              t(
                "requestHistoryDetail.declineRequest.title.text",
                "Request to decline the order has been sent"
              )
            );
            setShowPriceDisclaimer(true);
          }}
        />
      )}
    </>
  );
};

const MobileContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
`;

const MobileContainerParent = styled.View`
  padding: ${SpaceType.Big}px ${SpaceType.Double}px;
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
`;

const FullWidthRow = styled.View`
  width: 100%;
`;

const HalfRow = styled.View`
  flex-direction: row;
`;

const HalfButtonContainer = styled.View<{
  paddingLeft?: number;
  paddingRight?: number;
}>`
  flex: 1;
  padding-left: ${({ paddingLeft }) => paddingLeft ?? 0}px;
  padding-right: ${({ paddingRight }) => paddingRight ?? 0}px;
`;

const WebContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme["core-01"]};
`;

const WebContainerParent = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  padding: ${SpaceType.Big}px ${SpaceType.Double}px;
  border-radius: 8px;
`;

const RightButtonsContainer = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const ButtonFlexContainer = styled.View`
  padding-horizontal: 6px;
`;

export default SalesPersonButtonsContainer;
